import React, { useState, useEffect, useRef } from 'react';

const SimpleVoiceAssistant = () => {
 const [isListening, setIsListening] = useState(false);
 const [isSpeaking, setIsSpeaking] = useState(false);
 const [currentLanguage, setCurrentLanguage] = useState('en-US');
 const [lastMessage, setLastMessage] = useState('');
 
 const recognitionRef = useRef(null);

 const languages = [
 { code: 'en-US', name: 'English', flag: '🇺🇸' },
 { code: 'te-IN', name: 'Telugu', flag: '🇮🇳' },
 { code: 'hi-IN', name: 'Hindi', flag: '🇮🇳' }
 ];

 useEffect(() => {
 if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
 const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
 recognitionRef.current = new SpeechRecognition();
 recognitionRef.current.continuous = false;
 recognitionRef.current.interimResults = false;
 recognitionRef.current.lang = currentLanguage;

 recognitionRef.current.onstart = () => setIsListening(true);
 recognitionRef.current.onresult = async (event) => {
 const transcript = event.results[0][0].transcript;
 setLastMessage(transcript);
 await handleMessage(transcript);
 };
 recognitionRef.current.onerror = (event) => {
   console.error('Speech recognition error:', event.error);
   if (event.error === 'network') {
     alert("Speech recognition network error. Please check your internet connection or try again later.");
   }
   setIsListening(false);
 };
 recognitionRef.current.onend = () => setIsListening(false);
 }
 }, [currentLanguage]);

 const handleMessage = async (message) => {
 try {
 setIsSpeaking(true);
 const response = await getAIResponse(message, currentLanguage);
 speak(response);
 } catch (error) {
 console.error('Error:', error);
 speak('Sorry, I could not process your request.');
 }
 };

 const getAIResponse = async (message, language) => {
 const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
 if (!apiKey) {
 return 'Please add OpenAI API key to .env file';
 }

 const langName = language.includes('te') ? 'Telugu' : 
 language.includes('hi') ? 'Hindi' : 'English';

 try {
 const response = await fetch('https://api.openai.com/v1/chat/completions', {
 method: 'POST',
 headers: {
 'Authorization': `Bearer ${apiKey}`,
 'Content-Type': 'application/json'
 },
 body: JSON.stringify({
 model: 'gpt-3.5-turbo',
 messages: [
 { role: 'system', content: `Respond in ${langName} language only.` },
 { role: 'user', content: message }
 ],
 max_tokens: 100,
 temperature: 0.7
 })
 });

 if (response.ok) {
 const data = await response.json();
 return data.choices[0].message.content;
 }
 } catch (error) {
 console.error('OpenAI Error:', error);
 }
 
 return 'Error getting response';
 };

 const speak = (text) => {
 if ('speechSynthesis' in window) {
 window.speechSynthesis.cancel();
 const utterance = new SpeechSynthesisUtterance(text);
 utterance.lang = currentLanguage;
 utterance.onstart = () => setIsSpeaking(true);
 utterance.onend = () => setIsSpeaking(false);
 window.speechSynthesis.speak(utterance);
 }
 };

 const startListening = () => {
 if (recognitionRef.current && !isListening) {
 recognitionRef.current.start();
 }
 };

 const changeLanguage = (langCode) => {
 setCurrentLanguage(langCode);
 if (recognitionRef.current) {
 recognitionRef.current.lang = langCode;
 }
 };

 return (
 <div className="min-h-screen bg-bgSection p-8">
 <div className="max-w-2xl mx-auto">
 <h1 className="text-3xl font-bold text-white text-center mb-8">
 🤖 Voice Assistant
 </h1>

 {/* Language Selector */}
 <div className="bg-bgSection p-6 rounded-lg mb-6">
 <h3 className="text-white mb-4">Select Language</h3>
 <div className="flex gap-3">
 {languages.map((lang) => (
 <button
 key={lang.code}
 onClick={() => changeLanguage(lang.code)}
 className={`px-4 py-2 rounded ${
 currentLanguage === lang.code
 ? 'bg-blue-500 text-white'
 : 'bg-gray-700 text-white hover:bg-gray-600'
 }`}
 >
 {lang.flag} {lang.name}
 </button>
 ))}
 </div>
 </div>

 {/* Voice Control */}
 <div className="bg-bgSection p-6 rounded-lg mb-6 text-center">
 <button
 onClick={startListening}
 disabled={isListening || isSpeaking}
 className={`w-20 h-20 rounded-full text-3xl ${
 isListening
 ? 'bg-red-500 animate-pulse'
 : 'bg-green-500 hover:bg-green-600'
 } text-white ${isSpeaking ? 'opacity-50' : ''}`}
 >
 🎤
 </button>
 
 <p className="text-white mt-4">
 {isListening && 'Listening...'}
 {isSpeaking && 'Speaking...'}
 {!isListening && !isSpeaking && 'Click to speak'}
 </p>
 
 {lastMessage && (
 <p className="text-muted mt-2">Last: {lastMessage}</p>
 )}
 </div>

 {/* Setup Info */}
 <div className="bg-primary p-4 rounded-lg">
 <h3 className="text-primary font-bold mb-2">Setup Required</h3>
 <p className="text-primary text-sm">
 Add <code>REACT_APP_OPENAI_API_KEY=your_key</code> to .env file
 </p>
 </div>
 </div>
 </div>
 );
};

export default SimpleVoiceAssistant;








